import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Typography,
  Alert,
  Paper,
} from '@mui/material';
import { 
  CheckCircle, 
  MeetingRoom, 
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCurrentUser } from '../userSlice';
import OnboardingLayout from './OnboardingLayout';
import onboardingService from '../services/onboardingService';

export default function OnboardingCompletionStep() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  // Get onboarding status from Redux instead of separate API call
  const { user } = useSelector((state) => state.user);
  const onboardingStatus = user?.organization?.onboarding;
  
  const [error, setError] = useState('');
  const [isCompleting, setIsCompleting] = useState(false);
  const completionAttempted = useRef(false);

  // Define required steps
  const REQUIRED_STEPS = useMemo(() => ['welcome', 'billing'], []);

  const areAllRequiredStepsCompleted = useCallback(() => {
    if (!onboardingStatus?.completedSteps) {
      console.log('No completed steps found');
      return false;
    }
    console.log('Checking required steps:', REQUIRED_STEPS);
    console.log('Against completed steps:', onboardingStatus.completedSteps);
    const result = REQUIRED_STEPS.every(step => onboardingStatus.completedSteps.includes(step));
    console.log('All required steps completed?', result);
    return result;
  }, [onboardingStatus, REQUIRED_STEPS]);

  // Auto-complete onboarding when all required steps are done
  useEffect(() => {
    const autoCompleteOnboarding = async () => {
      // Only auto-complete if:
      // 1. We have onboarding status
      // 2. All required steps are completed
      // 3. Onboarding is not already completed
      // 4. We haven't already attempted completion
      if (onboardingStatus && 
          areAllRequiredStepsCompleted() && 
          !onboardingStatus.isCompleted && 
          !completionAttempted.current) {
        
        console.log('Auto-completing onboarding...');
        setIsCompleting(true);
        completionAttempted.current = true;

        try {
          await onboardingService.completeOnboarding();
          console.log('Onboarding auto-completed successfully');
          console.log('Refreshing user data...');
          await dispatch(fetchCurrentUser());
          console.log('User data refreshed');
        } catch (error) {
          console.error('Error auto-completing onboarding:', error);
          setError('Failed to complete onboarding automatically');
          completionAttempted.current = false;
        } finally {
          setIsCompleting(false);
        }
      }
    };

    autoCompleteOnboarding();
  }, [onboardingStatus, dispatch, areAllRequiredStepsCompleted]);

  const shouldShowCompletedView = () => {
    const isCompleted = onboardingStatus?.isCompleted;
    const allRequired = areAllRequiredStepsCompleted();
    console.log('Should show completed view? isCompleted:', isCompleted, 'allRequired:', allRequired);
    return isCompleted || allRequired;
  };

  const handleGoToRooms = () => {
    navigate('/rooms');
  };

  const getStepStatus = (step) => {
    if (!onboardingStatus) return 'pending';
    if (onboardingStatus.completedSteps?.includes(step)) return 'completed';
    if (onboardingStatus.skippedSteps?.includes(step)) return 'skipped';
    return 'pending';
  };

  // Show loading if user data is not yet loaded
  if (!user || !onboardingStatus) {
    return (
      <OnboardingLayout
        currentStep="completed"
        completedSteps={[]}
        title="Loading..."
      >
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography>Loading onboarding status...</Typography>
        </Box>
      </OnboardingLayout>
    );
  }

  const showCompletedView = shouldShowCompletedView();

  return (
    <OnboardingLayout
      currentStep="completed"
      completedSteps={onboardingStatus?.completedSteps || []}
      skippedSteps={onboardingStatus?.skippedSteps || []}
      title={showCompletedView ? "🎉 Welcome to Meeting Rooms!" : "🎯 Complete Your Setup"}
      subtitle={showCompletedView ? "Your workspace is ready!" : "You're one step away from completing your onboarding!"}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {!showCompletedView && (
        <Paper sx={{ p: 4, mb: 4, textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom>
            Almost There!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Please complete all required steps before finishing your onboarding.
          </Typography>
          
          {/* Show step completion status */}
          <Box sx={{ textAlign: 'left', mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Required Steps:</Typography>
            {REQUIRED_STEPS.map(step => (
              <Box key={step} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <CheckCircle 
                  sx={{ 
                    mr: 1, 
                    color: getStepStatus(step) === 'completed' ? 'success.main' : 'grey.300' 
                  }} 
                />
                <Typography 
                  sx={{ 
                    textDecoration: getStepStatus(step) === 'completed' ? 'line-through' : 'none',
                    color: getStepStatus(step) === 'completed' ? 'text.secondary' : 'text.primary'
                  }}
                >
                  {step.charAt(0).toUpperCase() + step.slice(1).replace('-', ' ')} Step
                </Typography>
              </Box>
            ))}
          </Box>

          <Button
            variant="outlined"
            onClick={() => navigate('/onboarding/welcome')}
          >
            Go Back to Complete Steps
          </Button>
        </Paper>
      )}

      {showCompletedView && (
        <>
          {/* Auto-completion indicator */}
          {isCompleting && (
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                Finalizing your onboarding setup...
              </Typography>
            </Alert>
          )}

          {/* Success Message */}
          <Paper sx={{ p: 4, mb: 4, bgcolor: 'success.50', textAlign: 'center' }}>
            <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h4" color="success.main" gutterBottom>
              Setup Complete!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Congratulations! You've successfully set up your meeting room management system.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              You're ready to start booking and managing your meeting rooms.
            </Typography>
          </Paper>

          {/* Primary Action */}
          <Box sx={{ textAlign: 'center' }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<MeetingRoom />}
              onClick={handleGoToRooms}
              sx={{ minWidth: 200, mb: 2 }}
            >
              Go to Rooms
            </Button>
            
            <Typography variant="body2" color="text.secondary">
              Need help? Check out our support documentation or contact our team.
            </Typography>
          </Box>
        </>
      )}
    </OnboardingLayout>
  );
} 